import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  RefreshControl,
  SafeAreaView,
  Alert,
} from '../utils/webComponents';
import { useNavigation } from '../utils/navigation';
import { useFocusEffect } from '../utils/navigation';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  School, 
  Person, 
  LocalFireDepartment, 
  Schedule, 
  Class, 
  AddCircle, 
  Delete, 
  LibraryBooks, 
  Style, 
  ChevronRight,
  GroupAdd,
} from '@mui/icons-material';
import { useFlashcards } from '../context/FlashcardContext';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../hooks/useTranslation';
import { useAuth } from '../context/AuthContext';
import { getFirestore, doc, updateDoc } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import OnboardingScreen from './OnboardingScreen';
import ReferralModal from '../components/ReferralModal';
import AddClassModal from '../components/AddClassModal';
import CustomAlert from '../components/CustomAlert';

const db = getFirestore(getApp());

const HomeScreen = () => {
  const navigation = useNavigation();
  const location = useLocation();
  const navigate = useNavigate();
  const { classes, user, loading, addClass, setCurrentClass, deleteClass, syncUserDataFromFirestore, setOnboarded } = useFlashcards();
  const { user: authUser } = useAuth();
  const [isDemoMode, setIsDemoMode] = React.useState(false);

  // Check if in demo mode - FOR DEMO: Always enable demo mode
  React.useEffect(() => {
    // Enable demo mode by default for demo purposes
    localStorage.setItem('isDemoMode', 'true');
    const demoMode = localStorage.getItem('isDemoMode') === 'true';
    setIsDemoMode(demoMode && !authUser);
  }, [authUser]);
  const { theme } = useTheme();
  const { t, tUI } = useTranslation();
  
  // Separate active and finished classes
  const activeClasses = classes.filter(cls => !cls.finished);
  const finishedClasses = classes.filter(cls => cls.finished);
  const [refreshing, setRefreshing] = useState(false);
  
  // Smart onboarding logic: Skip if user is marked as onboarded OR if they already have classes
  // This handles existing users who have classes but weren't marked as onboarded
  // FOR DEMO: Always skip onboarding
  const shouldShowOnboarding = false; // Disabled for demo
  const [showOnboarding, setShowOnboarding] = useState(false);
  
  const [deleteModal, setDeleteModal] = useState({ visible: false, classId: null, className: '' });
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [showAddClassModal, setShowAddClassModal] = useState(false);
  const adContainerRef = React.useRef(null);

  // Handle return from billing portal
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('from') === 'billing') {
      // Sync user data in case subscription status changed
      syncUserDataFromFirestore();
      
      // Replace the current history entry to prevent back navigation to billing portal
      // This removes the query parameter and prevents going back to Stripe
      navigate('/home', { replace: true });
    }
  }, [location.search, navigate, syncUserDataFromFirestore]);

  // Initialize Google AdSense ad
  useEffect(() => {
    const initAd = () => {
      if (adContainerRef.current) {
        // Check if ad is already initialized
        const existingAd = adContainerRef.current.querySelector('.adsbygoogle');
        if (existingAd && existingAd.hasAttribute('data-adsbygoogle-status')) {
          return; // Ad already initialized
        }

        // Create the ins element
        const insElement = document.createElement('ins');
        insElement.className = 'adsbygoogle';
        insElement.style.display = 'block';
        insElement.setAttribute('data-ad-client', 'ca-pub-7459593633881995');
        insElement.setAttribute('data-ad-slot', '2669766069');
        insElement.setAttribute('data-ad-format', 'auto');
        insElement.setAttribute('data-full-width-responsive', 'true');

        // Clear container and add the ins element
        adContainerRef.current.innerHTML = '';
        adContainerRef.current.appendChild(insElement);

        // Initialize the ad
        try {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        } catch (error) {
          console.error('Error loading ad:', error);
        }
      }
    };

    // Wait for AdSense script to load
    if (window.adsbygoogle) {
      // Script already loaded
      initAd();
    } else {
      // Wait for script to load
      const checkScript = setInterval(() => {
        if (window.adsbygoogle) {
          clearInterval(checkScript);
          initAd();
        }
      }, 100);

      // Timeout after 5 seconds
      setTimeout(() => {
        clearInterval(checkScript);
        if (window.adsbygoogle) {
          initAd();
        }
      }, 5000);

      return () => clearInterval(checkScript);
    }
  }, []);

  useEffect(() => {
    // FOR DEMO: Always skip onboarding
    setShowOnboarding(false);
    // Auto-mark as onboarded for demo
    if (!user?.isOnboarded) {
      setOnboarded(true);
    }
    
    // Auto-mark users with classes as onboarded (for existing users)
    if (!user?.isOnboarded && classes.length > 0 && authUser) {
      console.log('Auto-marking existing user as onboarded');
      setOnboarded(true);
      setShowOnboarding(false);
      
      // Also update Firestore
      const userDocRef = doc(db, 'users', authUser.uid);
      updateDoc(userDocRef, {
        isOnboarded: true,
      }).catch(error => {
        console.warn('Could not update onboarding status in Firestore:', error);
      });
    }
  }, [user?.isOnboarded, classes.length, setOnboarded, authUser]);

  const onRefresh = async () => {
    setRefreshing(true);
    setTimeout(() => setRefreshing(false), 1000);
  };

  const formatStudyTime = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('home.greeting.morning');
    if (hour < 17) return t('home.greeting.afternoon');
    return t('home.greeting.evening');
  };

  const getFirstName = () => {
    if (!user?.name) return 'there';
    return user.name.split(' ')[0];
  };

  const handleClassPress = (classItem) => {
    setCurrentClass(classItem);
    navigation.navigate('ClassDetail', { class: classItem });
  };

  const handleAddClass = () => {
    setShowAddClassModal(true);
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
  };

  const handleDeleteClass = (classId, className) => {
    setDeleteModal({ visible: true, classId, className });
  };

  const confirmDeleteClass = () => {
    if (deleteModal.classId) {
      deleteClass(deleteModal.classId);
      setDeleteModal({ visible: false, classId: null, className: '' });
    }
  };

  const cancelDeleteClass = () => {
    setDeleteModal({ visible: false, classId: null, className: '' });
  };

  const handleReferralShare = () => {
    setShowReferralModal(true);
  };

  if (showOnboarding) {
    return <OnboardingScreen isAddingClasses={user?.isOnboarded} onComplete={handleOnboardingComplete} />;
  }

  if (loading) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}>
        <View style={styles.loadingContainer}>
          <School sx={{ fontSize: 40, color: theme.primary }} />
          <Text style={[styles.loadingText, { color: theme.text }]}>Loading your data...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {/* App Title */}
        <View style={styles.appTitleContainer}>
          <Text style={[styles.appTitle, { color: theme.text }]}>SpacedAI</Text>
        </View>

        {/* Demo Mode Banner */}
        {isDemoMode && (
          <View style={[styles.demoBanner, { backgroundColor: theme.primary + '15', borderColor: theme.primary }]}>
            <Text style={[styles.demoBannerTitle, { color: theme.primary }]}>
              🎯 Demo Mode - 20 Free AI Flashcards
            </Text>
            <Text style={[styles.demoBannerText, { color: theme.text }]}>
              Try SpacedAI with 20 free AI flashcards! Sign up to get 150 AI flashcards and save your data permanently.
            </Text>
            <View style={styles.demoBannerButtons}>
              <TouchableOpacity
                style={[styles.demoBannerButton, { backgroundColor: theme.primary }]}
                onPress={() => {
                  localStorage.removeItem('isDemoMode');
                  navigation.navigate('Register');
                }}
              >
                <Text style={[styles.demoBannerButtonText, { color: 'white' }]}>
                  Sign Up Free
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.demoBannerButtonSecondary, { borderColor: theme.primary }]}
                onPress={() => {
                  localStorage.removeItem('isDemoMode');
                  navigation.navigate('Login');
                }}
              >
                <Text style={[styles.demoBannerButtonSecondaryText, { color: theme.primary }]}>
                  Login
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity
            style={[styles.profileButton, { backgroundColor: theme.surface }]}
            onPress={() => navigation.navigate('Profile')}
          >
            <Person sx={{ fontSize: 24, color: theme.primary }} />
          </TouchableOpacity>
        </View>

        {/* Stats Overview */}
        <View style={[styles.statsOverview, { backgroundColor: theme.surface, borderColor: theme.border }]}>
          <View style={[styles.overviewItem, { backgroundColor: theme.card }]}>
            <View style={[styles.overviewIcon, { backgroundColor: theme.primaryLight }]}>
              <LocalFireDepartment sx={{ fontSize: 24, color: theme.warning }} />
            </View>
            <Text style={[styles.overviewText, { color: theme.text }]}>{user?.streak || 0} {tUI('streak', 12)}</Text>
          </View>
          <View style={[styles.overviewItem, { backgroundColor: theme.card }]}>
            <View style={[styles.overviewIcon, { backgroundColor: theme.primaryLight }]}>
              <Schedule sx={{ fontSize: 24, color: theme.success }} />
            </View>
            <Text style={[styles.overviewText, { color: theme.text }]}>{formatStudyTime(user?.totalStudyTime || 0)}</Text>
          </View>
          <View style={[styles.overviewItem, { backgroundColor: theme.card }]}>
            <View style={[styles.overviewIcon, { backgroundColor: theme.primaryLight }]}>
              <Class sx={{ fontSize: 24, color: theme.primary }} />
            </View>
            <Text style={[styles.overviewText, { color: theme.text }]}>{classes.length} {tUI('classes', 10)}</Text>
          </View>
        </View>

        {/* Your Classes Section */}
        <View style={[styles.section, { backgroundColor: theme.surface, borderColor: theme.border }]}>
          <View style={styles.sectionHeader}>
            <Text style={[styles.sectionTitle, { color: theme.text }]}>{t('home.yourClasses')}</Text>
            {/* Hide add class button in demo mode */}
            {!isDemoMode && (
              <TouchableOpacity onPress={handleAddClass}>
                <AddCircle sx={{ fontSize: 24, color: theme.primary }} />
              </TouchableOpacity>
            )}
          </View>

          {activeClasses.length === 0 && finishedClasses.length === 0 ? (
            <View style={[styles.emptyState, { backgroundColor: theme.card, borderColor: theme.border }]}>
              <Class sx={{ fontSize: 64, color: theme.textMuted }} />
              <Text style={[styles.emptyTitle, { color: theme.text }]}>{t('home.noClasses')}</Text>
              <Text style={[styles.emptySubtitle, { color: theme.textSecondary }]}>
                {t('home.noClassesSubtitle')}
              </Text>
              <TouchableOpacity style={[styles.emptyButton, { backgroundColor: theme.primary }]} onPress={handleAddClass}>
                <Text style={[styles.emptyButtonText, { color: theme.buttonText || 'white' }]}>{t('home.addFirstClass')}</Text>
              </TouchableOpacity>
            </View>
          ) : activeClasses.length === 0 ? (
            <View style={styles.emptyState}>
              <Text style={[styles.emptySubtitle, { color: theme.textSecondary }]}>
                {t('home.noActiveClasses') || 'No active classes'}
              </Text>
            </View>
          ) : (
            <View style={styles.classesGrid} data-classes-grid="true">
              {activeClasses.map((classItem) => (
                <TouchableOpacity
                  key={classItem.id}
                  style={[styles.classCard, { borderLeftColor: classItem.color, backgroundColor: theme.card }]}
                  onPress={() => handleClassPress(classItem)}
                  data-card-hover="true"
                >
                  <View style={styles.classHeader}>
                    <View style={[styles.classColor, { backgroundColor: classItem.color }]} />
                    <Text style={[styles.className, { color: theme.text }]}>
                      {classItem.name}
                    </Text>
                    <TouchableOpacity
                      style={styles.deleteClassButton}
                      onPress={(e) => {
                        e.stopPropagation();
                        handleDeleteClass(classItem.id, classItem.name);
                      }}
                    >
                      <Delete sx={{ fontSize: 20, color: theme.error }} />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.classStats}>
                    <View style={styles.classStat}>
                      <LibraryBooks sx={{ fontSize: 16, color: theme.textSecondary }} />
                      <Text style={[styles.classStatText, { color: theme.textSecondary }]}>{classItem.totalSets || 0} {tUI('sets', 8)}</Text>
                    </View>
                    <View style={styles.classStat}>
                      <Style sx={{ fontSize: 16, color: theme.textSecondary }} />
                      <Text style={[styles.classStatText, { color: theme.textSecondary }]}>{classItem.totalCards || 0} {tUI('cards', 8)}</Text>
                    </View>
                  </View>
                  <View style={styles.classFooter}>
                    <Text style={[styles.classLastStudied, { color: theme.textMuted }]}>
                      {classItem.lastStudied
                        ? `${t('home.lastStudied')} ${new Date(classItem.lastStudied).toLocaleDateString()}`
                        : t('home.neverStudied')
                      }
                    </Text>
                    <ChevronRight sx={{ fontSize: 20, color: theme.textMuted }} />
                  </View>
                </TouchableOpacity>
              ))}
            </View>
          )}
        </View>

        {/* Finished Classes Section */}
        {finishedClasses.length > 0 && (
          <View style={[styles.section, { backgroundColor: theme.surface, borderColor: theme.border, marginTop: 16 }]}>
            <View style={styles.sectionHeader}>
              <Text style={[styles.sectionTitle, { color: theme.text }]}>{t('home.finishedClasses')}</Text>
            </View>
            <View style={styles.classesGrid} data-classes-grid="true">
              {finishedClasses.map((classItem) => (
                <TouchableOpacity
                  key={classItem.id}
                  style={[styles.classCard, { borderLeftColor: classItem.color, backgroundColor: theme.card }]}
                  onPress={() => handleClassPress(classItem)}
                  data-card-hover="true"
                >
                  <View style={styles.classHeader}>
                    <View style={[styles.classColor, { backgroundColor: classItem.color }]} />
                    <Text style={[styles.className, { color: theme.text }]}>
                      {classItem.name}
                    </Text>
                    <TouchableOpacity
                      style={styles.deleteClassButton}
                      onPress={(e) => {
                        e.stopPropagation();
                        handleDeleteClass(classItem.id, classItem.name);
                      }}
                    >
                      <Delete sx={{ fontSize: 20, color: theme.error }} />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.classStats}>
                    <View style={styles.classStat}>
                      <LibraryBooks sx={{ fontSize: 16, color: theme.textSecondary }} />
                      <Text style={[styles.classStatText, { color: theme.textSecondary }]}>{classItem.totalSets || 0} {tUI('sets', 8)}</Text>
                    </View>
                    <View style={styles.classStat}>
                      <Style sx={{ fontSize: 16, color: theme.textSecondary }} />
                      <Text style={[styles.classStatText, { color: theme.textSecondary }]}>{classItem.totalCards || 0} {tUI('cards', 8)}</Text>
                    </View>
                  </View>
                  <View style={styles.classFooter}>
                    <Text style={[styles.classLastStudied, { color: theme.textMuted }]}>
                      {classItem.lastStudied
                        ? `${t('home.lastStudied')} ${new Date(classItem.lastStudied).toLocaleDateString()}`
                        : t('home.neverStudied')
                      }
                    </Text>
                    <ChevronRight sx={{ fontSize: 20, color: theme.textMuted }} />
                  </View>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        )}

        {/* Delete Confirmation Modal */}
        <CustomAlert
          visible={deleteModal.visible}
          title={t('home.deleteClass')}
          message={t('home.deleteClassMessage')}
          buttons={[
            { text: t('common.cancel'), onPress: cancelDeleteClass, style: 'cancel' },
            { text: t('common.delete'), onPress: confirmDeleteClass, style: 'destructive' },
          ]}
          onClose={cancelDeleteClass}
        />

        {showReferralModal && (
          <ReferralModal
            visible={showReferralModal}
            onClose={() => setShowReferralModal(false)}
          />
        )}

        {/* Show add class modal */}
        {showAddClassModal && (
          <AddClassModal
            visible={showAddClassModal}
            onClose={() => setShowAddClassModal(false)}
          />
        )}

        {/* Google AdSense Ad */}
        <View style={styles.adContainer}>
          <div ref={adContainerRef} />
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 40,
    maxWidth: 1200,
    width: '100%',
    margin: '0 auto',
  },
  loadingContainer: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  loadingText: {
    fontSize: 18,
    marginTop: 16,
  },
  appTitleContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 20,
    paddingBottom: 8,
  },
  appTitle: {
    fontSize: 28,
    fontWeight: '700',
    letterSpacing: 1,
  },
  demoBanner: {
    marginHorizontal: 24,
    marginBottom: 20,
    padding: 20,
    borderRadius: 16,
    borderWidth: 2,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
  },
  demoBannerTitle: {
    fontSize: 18,
    fontWeight: '700',
    textAlign: 'center',
  },
  demoBannerText: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 1.5,
  },
  demoBannerButtons: {
    display: 'flex',
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  demoBannerButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 10,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  demoBannerButtonText: {
    fontSize: 14,
    fontWeight: '700',
  },
  demoBannerButtonSecondary: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 10,
    borderWidth: 2,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  demoBannerButtonSecondaryText: {
    fontSize: 14,
    fontWeight: '700',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'flex-start',
    paddingHorizontal: 24,
    paddingTop: 16,
    paddingBottom: 20,
  },
  headerTextContainer: {
    flex: 1,
  },
  greeting: {
    fontSize: 24,
    fontWeight: '600',
    marginBottom: 8,
    color: 'inherit',
    display: 'block',
  },
  subtitle: {
    fontSize: 24,
    fontWeight: '600',
    color: 'inherit',
    display: 'block',
  },
  profileButton: {
    width: 48,
    height: 48,
    borderRadius: 24,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'transform 0.2s, opacity 0.2s',
    cursor: 'pointer',
  },
  statsOverview: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginHorizontal: 24,
    marginTop: 16,
    marginBottom: 32,
    padding: 20,
    borderRadius: 16,
    borderWidth: 1,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    gap: 12,
  },
  overviewItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderRadius: 12,
    minWidth: 100,
    flex: 1,
  },
  overviewIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  overviewText: {
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
    lineHeight: 1.4,
  },
  section: {
    marginHorizontal: 24,
    marginTop: 0,
    marginBottom: 16,
    padding: 24,
    borderRadius: 16,
    borderWidth: 1,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  sectionHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 64,
    borderRadius: 16,
    borderWidth: 1,
    borderStyle: 'dashed',
    minHeight: 300,
    textAlign: 'center',
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '600',
    marginTop: 24,
    marginBottom: 12,
    textAlign: 'center',
    display: 'block',
  },
  emptySubtitle: {
    fontSize: 15,
    textAlign: 'center',
    marginBottom: 32,
    maxWidth: 400,
    lineHeight: 1.6,
    display: 'block',
  },
  emptyButton: {
    paddingVertical: 16,
    paddingHorizontal: 40,
    borderRadius: 12,
    minWidth: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    transition: 'opacity 0.2s, transform 0.1s',
  },
  emptyButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  classesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: 16,
  },
  classCard: {
    padding: 20,
    borderRadius: 16,
    borderLeftWidth: 4,
    borderWidth: 1,
    transition: 'transform 0.2s, box-shadow 0.2s',
    cursor: 'pointer',
  },
  classHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  classColor: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 8,
  },
  className: {
    flex: 1,
    fontSize: 18,
    fontWeight: '600',
  },
  deleteClassButton: {
    padding: 6,
    borderRadius: 6,
    transition: 'background-color 0.2s',
    cursor: 'pointer',
  },
  classStats: {
    display: 'flex',
    flexDirection: 'row',
    gap: 16,
    marginBottom: 12,
  },
  classStat: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  classStatText: {
    fontSize: 14,
  },
  classFooter: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  classLastStudied: {
    fontSize: 12,
  },
  adContainer: {
    marginTop: 32,
    marginBottom: 24,
    marginHorizontal: 24,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 100,
  },
});

export default HomeScreen;

